import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import PublicSummary from "@/PublicSummary";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import AuthCallback from "@/auth/AuthCallback";
import LoginPage from "@/auth/LoginPage";
import ProtectedRoute from "@/auth/ProtectedRoute";
import KipkLayout from "@/pages/KipkLayout";
import ManajemenAkun from "@/pages/ManajemenAkun";
import ManajemenPT from "@/pages/ManajemenPT";
import ManajemenTask from "@/pages/ManajemenTask";
import TaskArchive from "@/pages/TaskArchive";
import RegularTaskDetail from "@/pages/RegularTaskDetail";
import RegularTaskList from "@/pages/RegularTaskList";
import ProfilPengelola from "@/pages/ProfilPengelola";
import RekapPT from "@/pages/RekapPT";
import PTDashboard from "@/pages/PTDashboard";
import PTStudentList from "@/pages/PTStudentList";
import PTTaskArchive from "@/pages/PTTaskArchive";
import PadmaHub from "@/pages/PadmaHub";
import MantabLayout from "@/pages/mantab/MantabLayout";
import MantabDashboard from "@/pages/mantab/MantabDashboard";
import MantabDistribution from "@/pages/mantab/MantabDistribution";
import MantabBankAccounts from "@/pages/mantab/MantabBankAccounts";
import MantabStudentAttendance from "@/pages/mantab/MantabStudentAttendance";
import MantabQueueDesk from "@/pages/mantab/MantabQueueDesk";
import MantabQueueDisplay from "@/pages/mantab/MantabQueueDisplay";
import MantabBankProfile from "@/pages/mantab/MantabBankProfile";
import LaksmiLayout from "@/pages/laksmi/LaksmiLayout";
import LaksmiConsult from "@/pages/laksmi/LaksmiConsult";
import LaksmiAdmin from "@/pages/laksmi/LaksmiAdmin";
import LaksmiGuests from "@/pages/laksmi/LaksmiGuests";
import LaksmiHistory from "@/pages/laksmi/LaksmiHistory";
import LaksmiPublicPage from "@/pages/laksmi/LaksmiPublicPage";
import { Toaster } from "@/components/ui/sonner";
import { roleDestination } from "@/auth/roleDestination";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 60_000, refetchOnWindowFocus: false } },
});

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen" data-testid="home-redirect-loading">Memuat…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={roleDestination(user.role)} replace />;
}

function LaksmiRoleHome() {
  const { user } = useAuth();
  return ["master", "admin"].includes(user?.role) ? <LaksmiAdmin /> : <LaksmiConsult />;
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/ringkas" element={<PublicSummary />} />
            <Route path="/mantab/daftar-hadir" element={<MantabStudentAttendance />} />
            <Route path="/laksmi/konsultasi" element={<LaksmiPublicPage />} />
            <Route path="/mantab/display/:scheduleId" element={<ProtectedRoute allow={["bank_admin", "bank_operator"]}><MantabQueueDisplay /></ProtectedRoute>} />
            <Route path="/padma" element={<ProtectedRoute allow={["master", "admin", "pt"]}><PadmaHub /></ProtectedRoute>} />

            <Route path="/mantab" element={<ProtectedRoute allow={["master", "admin", "pt", "bank_admin", "bank_operator"]}><MantabLayout /></ProtectedRoute>}>
              <Route index element={<MantabDashboard />} />
              <Route path="kelola-distribusi" element={<MantabDistribution />} />
              <Route path="kelola-antrian/:scheduleId" element={<ProtectedRoute allow={["bank_admin", "bank_operator"]}><MantabQueueDesk /></ProtectedRoute>} />
              <Route path="akun-saya" element={<ProtectedRoute allow={["bank_admin", "bank_operator"]}><MantabBankProfile /></ProtectedRoute>} />
              <Route path="akun-bank" element={<ProtectedRoute allow={["master", "admin"]}><MantabBankAccounts /></ProtectedRoute>} />
              <Route path="akun-penyalur" element={<ProtectedRoute allow={["bank_admin"]}><MantabBankAccounts /></ProtectedRoute>} />
            </Route>

            <Route path="/laksmi" element={<ProtectedRoute allow={["master", "admin", "pt"]}><LaksmiLayout /></ProtectedRoute>}>
              <Route index element={<LaksmiRoleHome />} />
              <Route path="tamu" element={<ProtectedRoute allow={["master", "admin"]}><LaksmiGuests /></ProtectedRoute>} />
              <Route path="riwayat" element={<ProtectedRoute allow={["master", "admin"]}><LaksmiHistory /></ProtectedRoute>} />
            </Route>


            <Route path="/kipk" element={<ProtectedRoute allow={["master"]}><App /></ProtectedRoute>} />
            <Route element={<ProtectedRoute allow={["master", "admin"]}><KipkLayout /></ProtectedRoute>}>
              <Route path="/kipk/manajemen-akun" element={<ManajemenAkun />} />
              <Route path="/kipk/manajemen-pt" element={<ManajemenPT />} />
              <Route path="/kipk/manajemen-task" element={<ManajemenTask />} />
              <Route path="/kipk/manajemen-task/arsip" element={<TaskArchive />} />
              <Route path="/kipk/manajemen-task/reguler" element={<RegularTaskList />} />
              <Route path="/kipk/manajemen-task/reguler/:taskId" element={<RegularTaskDetail />} />
              <Route path="/kipk/rekap-pt" element={<RekapPT />} />
              <Route path="/kipk/pt/:id" element={<PTDashboard />} />
            </Route>

            <Route path="/pt" element={<ProtectedRoute allow={["pt"]}><PTDashboard /></ProtectedRoute>} />
            <Route path="/pt/profil" element={<ProtectedRoute allow={["pt"]}><ProfilPengelola /></ProtectedRoute>} />
            <Route path="/pt/mahasiswa" element={<ProtectedRoute allow={["pt"]}><PTStudentList /></ProtectedRoute>} />
            <Route path="/pt/arsip-task" element={<ProtectedRoute allow={["pt"]}><PTTaskArchive /></ProtectedRoute>} />

            <Route path="/" element={<HomeRedirect />} />
            <Route path="*" element={<HomeRedirect />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
